/**
 * Posiflora ERP settings API client (BFF: `/api/v1/store/settings/erp-posiflora`).
 *
 * MVP scope: exactly one ERP type (Posiflora). The connection + its sync capabilities are a
 * single resource (`GET`/`PUT`, always 200 — `configured: false` is a normal first-run state,
 * never an error); credentials are a separate write-only sub-resource (`PUT .../credentials`,
 * `409` until the connection has been saved at least once); disconnecting (`DELETE`) clears both
 * the connection and its credentials downstream.
 */
import { apiClient } from '@/shared/api';
import type {
  PosifloraSettings,
  TestConnectionResult,
  UpdatePosifloraCredentialsRequest,
  UpdatePosifloraSettingsRequest,
} from '../model/settingsTypes';

const BASE = '/api/v1/store/settings/erp-posiflora';

export const getPosifloraSettings = async (): Promise<PosifloraSettings> =>
  (await apiClient.get<PosifloraSettings>(BASE)).data;

/** Creates the connection on the first call, updates it thereafter — same endpoint either way. */
export const updatePosifloraSettings = async (
  body: UpdatePosifloraSettingsRequest,
): Promise<PosifloraSettings> => (await apiClient.put<PosifloraSettings>(BASE, body)).data;

/** `409` until the connection itself has been saved at least once (`configured: true`). */
export const updatePosifloraCredentials = async (
  body: UpdatePosifloraCredentialsRequest,
): Promise<PosifloraSettings> =>
  (await apiClient.put<PosifloraSettings>(`${BASE}/credentials`, body)).data;

export const testPosifloraConnection = async (): Promise<TestConnectionResult> =>
  (await apiClient.post<TestConnectionResult>(`${BASE}/test-connection`)).data;

/** Full disconnect — clears the connection and its credentials downstream. */
export const deletePosifloraSettings = async (): Promise<void> => {
  await apiClient.delete(BASE);
};
